/**
 * photography.js — Photography AI prompt tasks. Schema and quality bar: see _exemplar.js.
 * Most tasks are outputType "image": the rendered template IS the generation prompt.
 */
export const PHOTOGRAPHY_TASKS = [
  // ── Studio product shot (image) ───────────────────────────────────────────
  {
    id: "photography-studio-shot",
    category: "photography",
    title: "Create Studio Product Shot",
    description: "A photoreal studio product photograph of your package — choose background, surface, angle, lighting and props, and get a prompt ready for ChatGPT, Midjourney or Firefly.",
    outputType: "image",
    recommendedTools: ["chatgpt-images", "midjourney", "firefly"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, matte black, orange wordmark", required: true, brandKey: "productDescription", span: "full" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "background", label: "Background", type: "select", options: ["Pure white seamless", "Soft grey gradient", "Brand-color seamless", "Deep black", "Warm cream", "Soft pastel complementary to the pack"], default: "Soft grey gradient" },
      { id: "surface", label: "Surface", type: "select", options: ["Seamless paper (no visible surface)", "White marble", "Polished concrete", "Light oak wood", "Brushed metal", "Glossy acrylic with reflection", "Natural linen cloth"], default: "Seamless paper (no visible surface)" },
      { id: "angle", label: "Camera angle", type: "select", options: ["Straight-on at product height", "Three-quarter hero", "Slight low angle (heroic)", "45-degree high angle", "Top-down flat lay"], default: "Three-quarter hero" },
      { id: "lighting", label: "Lighting", type: "select", options: ["Soft diffused softbox", "Hard directional with crisp shadow", "Rim light on dark background", "Bright high-key", "Warm window light"], default: "Soft diffused softbox" },
      { id: "props", label: "Props", type: "select", options: ["None — product only", "Minimal ingredient props", "Condensation and ice (cold beverage)", "Product contents spilling out", "A single complementary object"], default: "None — product only" },
      { id: "aspectRatio", label: "Aspect ratio", type: "select", options: ["1:1", "4:5", "3:2", "16:9", "9:16"], default: "1:1" },
    ],
    template: `Act as a senior commercial product photographer who shoots packaging for CPG brands' websites and ad campaigns. Generate one photorealistic studio product photograph.

SUBJECT
{{brandName}}, {{category|article}}: {{productDescription}}.
The packaging is the hero — real-world proportions, crisp label edges, believable material and finish, the brand name "{{brandName}}" spelled exactly and perfectly legible on the front panel.
{{#if claims}}
Small supporting text on pack only: {{claims|join}}. No other words.
{{/if}}
{{#if brandColors}}
Pack colors: {{brandColors|join}}.
{{/if}}

SET
Background: {{background}}. Surface: {{surface}}.
{{#if props=Minimal ingredient props}}
Props: two or three real ingredients relevant to {{category|article}}, placed naturally at the base, slightly out of focus, never covering the pack.
{{/if}}
{{#if props=Condensation and ice (cold beverage)}}
Props: fine realistic condensation droplets on the pack, a few clear ice cubes and a subtle splash of water at the base, product still fully legible.
{{/if}}
{{#if props=Product contents spilling out}}
Props: the actual product contents spilling naturally from the open pack onto the surface, styled and appetizing.
{{/if}}
{{#if props=A single complementary object}}
Props: one complementary object that fits the brand's world, placed off to one side and smaller than the product.
{{/if}}
{{#if props=None — product only}}
No props — the product stands alone.
{{/if}}

CAMERA AND LIGHT
Angle: {{angle}}.
Lighting: {{lighting}}, soft contact shadow grounding the product, a clean specular highlight along the pack edge, no blown-out hot spots.
Shot on a full-frame camera with a 100mm macro lens, f/8, tack-sharp across the whole product, true-to-life color, 8k detail, commercial advertising quality.

MOOD
{{style|join}}{{#unless style}}Premium, modern, minimal{{/unless}}. Generous negative space around the product for later text overlays.

{{#if avoid}}
AVOID
{{avoid|list}}
- Warped, duplicated or misspelled text
- Extra products, hands or floating objects
- Distracting backgrounds, heavy filters, fake-looking CGI sheen
{{/if}}
{{#unless avoid}}
AVOID
- Warped, duplicated or misspelled text
- Extra products, hands or floating objects
- Distracting backgrounds, heavy filters, fake-looking CGI sheen
{{/unless}}

NEGATIVE PROMPT
blurry, low resolution, misspelled text, distorted packaging, cartoon, illustration, watermark, cluttered, oversaturated, extra objects, deformed

ASPECT RATIO
{{aspectRatio}}`,
    tips: [
      "Upload your product photo or logo first, then paste this prompt — ChatGPT will keep your real pack design.",
      "Generate the same setup at 1:1, 4:5 and 16:9 so you have a matching set for Amazon, Instagram and your website hero.",
      "Use Midjourney with --style raw for the most photographic look; use ChatGPT when the label text must be exact.",
    ],
  },

  // ── Lifestyle photography (image) ─────────────────────────────────────────
  {
    id: "photography-lifestyle",
    category: "photography",
    title: "Create Lifestyle Photography",
    description: "An on-brand lifestyle scene with your product in a real moment — pick the occasion, setting, season and whether people appear, and get a photoreal image prompt.",
    outputType: "image",
    recommendedTools: ["chatgpt-images", "midjourney", "firefly"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, matte black, orange wordmark", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 25–40 into fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Clean performance energy", brandKey: "positioning", span: "full" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Gym-bro clichés; neon", brandKey: "avoid" },
      { id: "occasion", label: "Occasion", type: "select", options: ["Morning routine", "Post-workout", "Desk / work break", "Outdoor adventure", "Picnic or social gathering", "Evening wind-down", "On-the-go commute", "Family kitchen moment"], default: "Post-workout" },
      { id: "setting", label: "Setting", type: "select", options: ["Bright modern kitchen", "Home gym or studio", "City street or rooftop", "Trailhead or mountain overlook", "Beach or lakeside", "Cozy living room", "Café table", "Car or transit"], default: "Home gym or studio" },
      { id: "modelPresence", label: "People in the shot", type: "select", options: ["No people — product in the scene", "Hands only", "One person, candid", "Two people", "Small group, social"], default: "Hands only" },
      { id: "season", label: "Season / light", type: "select", options: ["Summer, bright sun", "Golden-hour warmth", "Crisp autumn", "Soft winter daylight", "Fresh spring morning", "Overcast, even light"], default: "Golden-hour warmth" },
      { id: "aspectRatio", label: "Aspect ratio", type: "select", options: ["4:5", "1:1", "3:2", "16:9", "9:16"], default: "4:5" },
    ],
    template: `Act as a senior lifestyle photographer who shoots editorial campaigns for premium food and beverage brands. Generate one candid, photorealistic lifestyle photograph.

PRODUCT
{{brandName}}, {{category|article}}: {{productDescription}}.
The product is clearly visible and in focus, label facing camera, brand name "{{brandName}}" legible and spelled exactly, occupying roughly one quarter to one third of the frame — present but not staged like an ad.
{{#if brandColors}}
Pack colors: {{brandColors|join}} — let them echo subtly in the scene (a towel, a wall, a piece of clothing).
{{/if}}

MOMENT
Occasion: {{occasion|lower}}. Setting: {{setting|lower}}. Season and light: {{season|lower}}.
{{#if modelPresence=No people — product in the scene}}
No people. The product sits naturally where someone just left it — a half-used gym bag, an open laptop, a picnic blanket — with signs of life but no hands or faces.
{{/if}}
{{#if modelPresence=Hands only}}
Only hands visible: a natural hand holding or reaching for the product, realistic skin texture, no faces, no watches or jewelry that date the image.
{{/if}}
{{#if modelPresence=One person, candid}}
One person who looks like the target consumer, mid-action and unposed, face partly turned or naturally lit, authentic expression, wearing simple clothing in muted tones.
{{/if}}
{{#if modelPresence=Two people}}
Two people who look like the target consumer, interacting naturally with each other rather than with the camera, one holding the product.
{{/if}}
{{#if modelPresence=Small group, social}}
Three to four people who look like the target consumer, relaxed and mid-conversation, the product on the table or in hand, nobody looking at the camera.
{{/if}}

WHO THIS IS FOR
{{targetConsumer}}
{{#if positioning}}
The image should make this feel true: {{positioning}}
{{/if}}

PHOTOGRAPHY STYLE
{{style|join}}{{#unless style}}Premium, modern, natural{{/unless}}. Editorial documentary feel, natural light, shallow depth of field, soft film-like color grade, real textures, slight imperfection that reads as authentic.
Shot on a 35mm or 50mm lens, f/2, product tack-sharp, background softly blurred, natural skin tones, no HDR, no heavy retouching.

{{#if avoid}}
AVOID
{{avoid|list}}
- Stock-photo poses, forced smiles, looking at camera
- Warped or misspelled pack text, duplicated products
- Over-styled, cluttered or obviously artificial scenes
{{/if}}
{{#unless avoid}}
AVOID
- Stock-photo poses, forced smiles, looking at camera
- Warped or misspelled pack text, duplicated products
- Over-styled, cluttered or obviously artificial scenes
{{/unless}}

NEGATIVE PROMPT
stock photo, posed, fake smile, misspelled text, distorted packaging, extra fingers, plastic skin, oversaturated, HDR, watermark, cartoon, illustration

ASPECT RATIO
{{aspectRatio}}`,
    tips: [
      "Upload your product photo first so ChatGPT places your actual pack in the scene instead of inventing one.",
      "Midjourney gives the most beautiful lifestyle light — add --style raw --ar 4:5 and then composite your real pack in Photoshop or ChatGPT.",
      "Generate 4 occasions from this one prompt by changing only the Occasion field — instant content variety for a month of posts.",
    ],
  },

  // ── Ingredient & texture imagery (image) ──────────────────────────────────
  {
    id: "photography-ingredient",
    category: "photography",
    title: "Create Ingredient & Texture Imagery",
    description: "Appetizing macro and ingredient-story imagery built around your hero ingredients — for pack backs, websites, Amazon modules and social.",
    outputType: "image",
    recommendedTools: ["midjourney", "chatgpt-images", "firefly"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee, L-theanine, Yuzu", required: true, brandKey: "ingredients" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Cartoonish fruit; rainbow colors", brandKey: "avoid" },
      { id: "composition", label: "Composition", type: "select", options: ["Macro texture close-up (no product)", "Ingredients arranged around the product", "Splash or pour action with ingredients", "Flat-lay ingredient story", "Cross-section or cut-open ingredient"], default: "Ingredients arranged around the product" },
      { id: "surface", label: "Surface", type: "select", options: ["Dark slate", "White marble", "Raw wood", "Brand-color seamless", "Rustic linen", "Wet stone"], default: "Dark slate" },
      { id: "lighting", label: "Lighting", type: "select", options: ["Dramatic side light (dark and moody)", "Bright natural daylight", "Soft overhead studio", "Backlit with glow"], default: "Dramatic side light (dark and moody)" },
      { id: "aspectRatio", label: "Aspect ratio", type: "select", options: ["1:1", "4:5", "3:2", "16:9"], default: "1:1" },
    ],
    template: `Act as a senior food and beverage photographer specializing in macro ingredient and texture photography for premium CPG brands. Generate one photorealistic, appetizing image.

HERO INGREDIENTS
{{ingredients|join}} — shown as real, fresh, whole or freshly cut ingredients with true-to-life color, visible texture (pores, fibers, droplets, crystals, grain), nothing artificial or plastic-looking.

COMPOSITION
{{composition}}.
{{#if composition=Macro texture close-up (no product)}}
Extreme close-up filling the frame with the ingredient texture, no packaging, abstract enough to work as a background, sharp detail at the focal point fading to soft blur.
{{/if}}
{{#if composition=Ingredients arranged around the product}}
{{brandName}}, {{category|article}}{{#if productDescription}} ({{productDescription}}){{/if}}, centered and in sharp focus with the brand name "{{brandName}}" legible; the ingredients arranged naturally around its base, some whole, some cut, a few scattered pieces for movement.
{{/if}}
{{#if composition=Splash or pour action with ingredients}}
{{brandName}}, {{category|article}}, with a frozen high-speed splash or pour; ingredients suspended mid-air in the liquid, droplets crisp, brand name "{{brandName}}" still legible on the pack.
{{/if}}
{{#if composition=Flat-lay ingredient story}}
Top-down flat lay: {{brandName}} pack{{#if productDescription}} ({{productDescription}}){{/if}} at one edge, the ingredients laid out in a loose grid or arc with breathing room, a hint of process (a spoon, a mortar, a cutting board).
{{/if}}
{{#if composition=Cross-section or cut-open ingredient}}
A clean cross-section of the primary ingredient as the hero, juice or interior texture visible, the {{brandName}} pack small and soft-focused in the background.
{{/if}}

SET AND LIGHT
Surface: {{surface|lower}}. Lighting: {{lighting|lower}}, emphasizing texture and freshness, fine water droplets where natural, no harsh hot spots.
{{#if brandColors}}
Color story: {{brandColors|join}} — let the ingredients' natural colors harmonize with this palette rather than fight it.
{{/if}}
{{#if claims}}
The image should quietly support: {{claims|join}} — natural, clean, real.
{{/if}}

CAMERA
100mm macro lens, f/4, razor-sharp focus on the hero texture, creamy background falloff, 8k detail, color-accurate, editorial food photography quality.

MOOD
{{style|join}}{{#unless style}}Premium, modern, natural{{/unless}}. Fresh, honest, appetizing.

{{#if avoid}}
AVOID
{{avoid|list}}
- Fake, glossy or cartoon-looking ingredients
- Ingredients that are not in the product
- Misspelled or warped pack text, extra packs
{{/if}}
{{#unless avoid}}
AVOID
- Fake, glossy or cartoon-looking ingredients
- Ingredients that are not in the product
- Misspelled or warped pack text, extra packs
{{/unless}}

NEGATIVE PROMPT
plastic, waxy, cartoon, illustration, oversaturated, blurry, misspelled text, distorted packaging, watermark, cluttered, artificial colors

ASPECT RATIO
{{aspectRatio}}`,
    tips: [
      "Midjourney is strongest for texture and mood here; upload your pack to ChatGPT when the product needs to appear with exact text.",
      "Generate one macro texture per hero ingredient at 16:9 — they make perfect website section backgrounds and Amazon A+ banners.",
    ],
  },

  // ── E-commerce image set (text plan with prompts) ─────────────────────────
  {
    id: "photography-ecommerce-set",
    category: "photography",
    title: "Create E-commerce Image Set",
    description: "A complete 7-image Amazon / Shopify listing plan — main white-background shot, infographic, lifestyle, comparison, size, ingredients and reviews — each with its own image prompt and overlay copy.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "listingUnit", label: "Listing unit / pack size", type: "text", placeholder: "12-pack of 12 oz cans", required: true },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 25–40 into fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Clean performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee, L-theanine", brandKey: "ingredients" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "platform", label: "Platform", type: "select", options: ["Amazon", "Shopify / DTC", "Both"], default: "Amazon" },
    ],
    template: `Act as a senior Amazon and Shopify creative director who has built listing image stacks for CPG brands and knows which images lift conversion and which get suppressed.

Plan a complete 7-image listing set for {{brandName}}, {{category|article}}, sold as {{listingUnit}}. Platform: {{platform}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
Price: {{pricePoint}}.
{{/if}}

TARGET CONSUMER
{{targetConsumer}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if claims}}
KEY CLAIMS
{{claims|list}}
{{/if}}

{{#if ingredients}}
HERO INGREDIENTS
{{ingredients|list}}
{{/if}}

{{#if competitors}}
COMPETITORS SHOPPERS WILL COMPARE US TO
{{competitors|join}}.
{{/if}}

VISUAL SYSTEM
Style: {{style|join}}{{#unless style}}premium, modern, minimal{{/unless}}.
{{#if brandColors}}
Brand colors for overlays and backgrounds: {{brandColors|join}}.
{{/if}}

PLATFORM RULES TO RESPECT
- Amazon main image: pure white background (RGB 255,255,255), the product fills about 85% of the frame, no text, logos, badges, props or people, the full product visible, minimum 1600 px on the longest side for zoom. Flag that Amazon's current image policy should be checked before upload.
- Secondary images: text overlays allowed; keep text large enough to read on a phone thumbnail; square 1:1 canvas; no claims that cannot be substantiated.
{{#if platform!=Amazon}}
- Shopify / DTC: same image set works, but the first image can be a branded background; keep a consistent frame across the set.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Medical or disease claims in any overlay
- Competitor logos or names in comparison images
{{/if}}
{{#unless avoid}}
AVOID
- Medical or disease claims in any overlay
- Competitor logos or names in comparison images
{{/unless}}

PROVIDE, FOR EACH OF THE 7 IMAGES
Image 1 — Main image (pure white background, product only)
Image 2 — Benefits infographic (3–4 key claims with icons)
Image 3 — Lifestyle (product in use by the target consumer)
Image 4 — Comparison chart ("us vs. typical {{category|lower}}", no competitor names)
Image 5 — Size and pack contents ({{listingUnit}}, dimensions, what's in the box)
Image 6 — Ingredients story (hero ingredients and what they are for, no health outcome claims)
Image 7 — Social proof (review quote layout with placeholder for a real verified review)

For each image give:
1. Purpose and the shopper question it answers
2. Layout description (where the product sits, where text goes, background)
3. Exact overlay copy (headline max 6 words, supporting line max 12 words, any labels) — write "NONE" for Image 1
4. A detailed image-generation prompt for ChatGPT image generation, Midjourney or Firefly that renders the photo element, including "{{brandName}}" legible on pack, aspect ratio 1:1, and a negative-prompt line
5. Production notes (what to build in Canva or Photoshop vs. generate, file size, where real data must replace placeholders)

Then provide: a table of the full set (image # | type | headline | prompt tool), the recommended order, and three A/B test ideas for the main and second images.

Mark every claim in the overlay copy that needs substantiation or regulatory review with [VERIFY]. Never invent review quotes — leave a clearly marked placeholder.`,
    tips: [
      "Shoot Image 1 for real if you can (a phone on a white sheet, then remove.bg) — Amazon rejects renders that look fake. Use the prompts for Images 2–7.",
      "Paste each prompt into ChatGPT with your product photo attached, then add the overlay copy in Canva on a 2000 x 2000 px canvas.",
      "Follow-up: “Write the Amazon title, 5 bullets and A+ module copy to match this image set.”",
    ],
  },

  // ── Retailer presentation imagery (image) ─────────────────────────────────
  {
    id: "photography-retailer-presentation",
    category: "photography",
    title: "Create Retailer Presentation Imagery",
    description: "Buyer-deck imagery that shows your product in the retailer's own environment — hero shot, shelf set, end-cap or cooler door — in a 16:9 slide-ready frame.",
    outputType: "image",
    recommendedTools: ["chatgpt-images", "midjourney", "firefly"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, matte black, orange wordmark", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Clean performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "retailer", label: "Retailer environment", type: "select", options: ["Natural grocery (Whole Foods / Sprouts style)", "Conventional grocery", "Convenience store", "Mass retail (Target / Walmart style)", "Club store (Costco style)", "Specialty / gourmet shop", "Gym or fitness retail"], default: "Natural grocery (Whole Foods / Sprouts style)" },
      { id: "shotType", label: "Shot type", type: "select", options: ["Hero shot on brand backdrop", "Shelf set in the retailer aisle", "End-cap or floor display", "Cooler door", "Checkout / impulse placement"], default: "Shelf set in the retailer aisle" },
      { id: "skuCount", label: "SKUs to show", type: "select", options: ["1", "2", "3", "4", "6"], default: "3" },
      { id: "aspectRatio", label: "Aspect ratio", type: "select", options: ["16:9", "3:2", "4:3"], default: "16:9" },
    ],
    template: `Act as a senior retail photographer producing imagery for a CPG brand's buyer presentation. Generate one photorealistic, slide-ready image that makes a category buyer picture this product in their store.

PRODUCT
{{brandName}}, {{category|article}}: {{productDescription}}. Show {{skuCount}} SKU{{#if skuCount!=1}}s{{/if}} of the range, each with the brand name "{{brandName}}" spelled exactly and legible, clean and perfectly faced.
{{#if brandColors}}
Pack colors: {{brandColors|join}}.
{{/if}}
{{#if claims}}
Small on-pack claim text only: {{claims|join}}.
{{/if}}

SHOT
{{shotType}}, inside {{retailer|article}}.
{{#if shotType=Hero shot on brand backdrop}}
The products on a seamless backdrop in a brand color, lit like a premium campaign, with a subtle hint of the store environment blurred far behind — polished, confident, ready for a title slide.
{{/if}}
{{#if shotType=Shelf set in the retailer aisle}}
The products blocked together at eye level on the retailer's actual shelving style, realistic shelf tags, surrounded by generic unbranded {{category|lower}} neighbors{{#if competitors}} in the visual style of {{competitors|join}} but with no real logos{{/if}}, showing how the range commands the shelf.
{{/if}}
{{#if shotType=End-cap or floor display}}
A branded corrugated end-cap or floor display fully stocked with the products, a simple header card reading "{{brandName}}", positioned at the end of an aisle in the retailer's environment, a shopper softly blurred in the background.
{{/if}}
{{#if shotType=Cooler door}}
The products on a wire shelf behind a glass cooler door, light condensation, cool LED lighting, a branded door cling reading "{{brandName}}", surrounded by generic cold beverages.
{{/if}}
{{#if shotType=Checkout / impulse placement}}
The products in a small countertop display or checkout rack beside the register, warm store lighting, a shopper's hand reaching for one.
{{/if}}
{{#if retailer=Natural grocery (Whole Foods / Sprouts style)}}
Environment cues: warm wood shelving, chalkboard-style signage, natural light, premium and clean.
{{/if}}
{{#if retailer=Mass retail (Target / Walmart style)}}
Environment cues: long white gondola shelving, bright even lighting, wide clean aisle.
{{/if}}
{{#if retailer=Club store (Costco style)}}
Environment cues: warehouse racking, pallet and tray presentation, high ceilings, industrial light.
{{/if}}
{{#if retailer=Convenience store}}
Environment cues: compact aisle, bright cool light, dense shelving, coolers in the background.
{{/if}}
{{#if retailer=Gym or fitness retail}}
Environment cues: front-desk retail shelf, rubber floor, equipment blurred behind, energetic light.
{{/if}}

{{#if positioning}}
FEELING
The image should make a buyer believe: {{positioning}}
{{/if}}
Style: {{style|join}}{{#unless style}}premium, modern, minimal{{/unless}}.

CAMERA
Shot at shopper eye level, 35mm lens, f/4, products in crisp focus, environment slightly softer, natural perspective, clean highlights, realistic in-store photography, room on one side of the frame for slide text.

{{#if avoid}}
AVOID
{{avoid|list}}
- Real retailer logos, signage text or competitor trademarks
- Misspelled or warped pack text, duplicated packs
- Empty shelves, messy facings, people blocking the product
{{/if}}
{{#unless avoid}}
AVOID
- Real retailer logos, signage text or competitor trademarks
- Misspelled or warped pack text, duplicated packs
- Empty shelves, messy facings, people blocking the product
{{/unless}}

NEGATIVE PROMPT
blurry, misspelled text, distorted packaging, real brand logos, watermark, cartoon, illustration, fisheye, empty shelf, cluttered

ASPECT RATIO
{{aspectRatio}}`,
    tips: [
      "Upload your product image first so the packs in the scene are yours. Generate one image per retailer you are pitching.",
      "Drop the result into the Retail AI buyer deck as the opening slide — buyers respond to seeing the product already in their store.",
    ],
  },

  // ── Social content image set (text plan with prompts) ─────────────────────
  {
    id: "photography-social-set",
    category: "photography",
    title: "Create Social Content Images",
    description: "Six on-brand image prompts for feed and stories — each with content pillar, aspect ratio, overlay copy and caption hook — enough visual variety for two weeks of posting.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 25–40 into fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Clean performance energy", brandKey: "positioning", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee, L-theanine", brandKey: "ingredients" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "platform", label: "Platform", type: "select", options: ["Instagram feed + stories", "TikTok", "Pinterest", "All of the above"], default: "Instagram feed + stories" },
      { id: "contentMix", label: "Content mix", type: "select", options: ["Balanced", "Product-heavy", "Lifestyle-heavy", "Education and ingredients"], default: "Balanced" },
      { id: "theme", label: "Theme or moment (optional)", type: "text", placeholder: "Back to school, Summer hydration, Launch week" },
    ],
    template: `Act as a senior social creative director for challenger CPG brands who plans image content that stops the scroll and still looks like one brand.

Plan six on-brand images for {{brandName}}, {{category|article}}, for {{platform}}. Content mix: {{contentMix|lower}}.{{#if theme}} Theme: {{theme}}.{{/if}}

PRODUCT
{{productDescription}}

TARGET CONSUMER
{{targetConsumer}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if personality}}
PERSONALITY
{{personality|join}}.
{{/if}}

{{#if claims}}
CLAIMS WE CAN SHOW
{{claims|list}}
{{/if}}

{{#if ingredients}}
HERO INGREDIENTS
{{ingredients|list}}
{{/if}}

VISUAL SYSTEM
Style: {{style|join}}{{#unless style}}premium, modern, minimal{{/unless}}.
{{#if brandColors}}
Brand colors: {{brandColors|join}} — use them consistently in backgrounds and overlays so the grid reads as one brand.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Generic stock-photo looks
- Text overlays with health or performance promises
{{/if}}
{{#unless avoid}}
AVOID
- Generic stock-photo looks
- Text overlays with health or performance promises
{{/unless}}

THE SIX SLOTS
1. Hero product — clean studio shot (feed, 4:5)
2. Lifestyle moment — product in the consumer's real life (feed, 4:5)
3. Ingredient or "what's inside" — macro or flat lay (feed, 1:1)
4. Education or myth-bust — graphic-led with product (feed, 4:5)
5. Story or Reel cover — vertical, bold, thumb-stopping (stories, 9:16)
6. Community or UGC-style — phone-shot feel, real moment (stories or feed, 9:16 or 4:5)
Adjust the mix toward {{contentMix|lower}} while keeping all six visually consistent.

FOR EACH SLOT PROVIDE
1. Content pillar and the single idea of the post
2. Aspect ratio and placement
3. A detailed image-generation prompt (ChatGPT image generation, Midjourney or Firefly): subject, setting, lighting, lens, mood, brand colors, "{{brandName}}" legible on pack where the product appears, and a negative-prompt line
4. Overlay copy, if any (max 6 words) and where it sits so it stays clear of the platform UI
5. Caption hook (first line only, under 12 words) and a suggested CTA
6. Which tool to use (ChatGPT for exact pack text, Midjourney for mood) and one tip for that slot

Finish with: a 2 x 3 grid preview described in words (how the six look side by side), a posting order over two weeks, and a note on any overlay text that needs regulatory review before posting.`,
    tips: [
      "Paste each prompt into ChatGPT with your product photo attached; batch all six in one chat so the style stays consistent.",
      "Follow-up: “Write the full captions for all six in my brand voice, with 5 hashtags each.”",
      "Resize and caption everything in CapCut or Canva — slot 5 also works as the first frame of a Reel.",
    ],
  },

  // ── Edit my product photo (image edit instruction) ────────────────────────
  {
    id: "photography-edit-photo",
    category: "photography",
    title: "Edit My Product Photo",
    description: "An instruction prompt for ChatGPT image editing that turns a basic phone photo of your product into a clean, professional shot — fix lighting, remove the background or place it in a scene.",
    outputType: "image",
    recommendedTools: ["chatgpt-images", "firefly", "photoshop"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, matte black, orange wordmark", required: true, brandKey: "productDescription", span: "full" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 25–40 into fitness", brandKey: "targetConsumer", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "editGoal", label: "What do you want done?", type: "select", options: ["Clean pure-white background (Amazon-ready)", "Place it in a lifestyle scene", "Fix lighting and color only", "Premium studio look with soft reflection", "Add ingredients or props around it"], default: "Clean pure-white background (Amazon-ready)" },
      { id: "photoIssues", label: "What is wrong with the photo?", type: "tags", placeholder: "Yellow kitchen light, busy background, slight blur, crooked angle", help: "List everything you notice." },
      { id: "sceneDescription", label: "Scene to place it in (if applicable)", type: "textarea", placeholder: "A sunlit kitchen counter next to a gym bag, morning light", span: "full" },
      { id: "lighting", label: "Target lighting", type: "select", options: ["Soft diffused studio", "Bright natural daylight", "Warm golden hour", "Dramatic rim light"], default: "Soft diffused studio" },
      { id: "aspectRatio", label: "Output aspect ratio", type: "select", options: ["Keep original", "1:1", "4:5", "3:2", "16:9"], default: "1:1" },
    ],
    template: `Act as a senior product retoucher and commercial photographer. I have uploaded a basic phone photo of my product. Edit this exact photo — do not generate a new product.

THE PRODUCT IN THE PHOTO
{{brandName}}, {{category|article}}: {{productDescription}}.
Keep the packaging exactly as photographed: same shape, proportions, label layout, logo, colors and all text — the brand name "{{brandName}}" must remain exactly as it appears, with no respelling, redrawing or added words.

{{#if photoIssues}}
PROBLEMS TO FIX
{{photoIssues|list}}
{{/if}}

THE EDIT
{{editGoal}}.
{{#if editGoal=Clean pure-white background (Amazon-ready)}}
Cut the product out cleanly along its true edges, place it on a pure white background (RGB 255,255,255), center it so it fills about 85% of the frame, add a soft natural contact shadow beneath it, remove every other object. No text, props or graphics anywhere.
{{/if}}
{{#if editGoal=Place it in a lifestyle scene}}
Cut the product out cleanly and composite it into this scene: {{sceneDescription|default:a bright, clean setting that fits the brand}}. Match the perspective, scale, light direction, color temperature and shadows so it looks photographed in place. Keep the label facing camera and fully legible.
{{#if targetConsumer}}
The scene should feel right for: {{targetConsumer}}
{{/if}}
{{/if}}
{{#if editGoal=Fix lighting and color only}}
Keep the original background and composition. Correct white balance to neutral, lift exposure evenly, recover highlight detail, reduce noise, straighten the horizon, sharpen the label, and make the pack colors accurate{{#if brandColors}} ({{brandColors|join}}){{/if}}.
{{/if}}
{{#if editGoal=Premium studio look with soft reflection}}
Cut the product out and place it on a seamless studio background with a subtle gradient, a soft mirror-like reflection beneath it, a clean edge highlight and a gentle contact shadow. Remove all clutter.
{{/if}}
{{#if editGoal=Add ingredients or props around it}}
Keep the product in place, clean up the background, and add realistic, relevant ingredients or props arranged naturally around the base — smaller than the product, never covering the label. {{#if sceneDescription}}Props and setting: {{sceneDescription}}.{{/if}}
{{/if}}

LIGHT AND COLOR
Relight the scene as {{lighting|lower}}: even, flattering light on the front panel, a single soft highlight along the pack edge, true-to-life colors, neutral white balance.
{{#if brandColors}}
Brand colors to keep accurate: {{brandColors|join}}.
{{/if}}

FINISH
{{style|join}}{{#unless style}}Premium, modern, minimal{{/unless}}. Commercial product-photography quality, sharp and clean, high resolution, natural-looking, no visible editing artifacts or halos around the cutout.
{{#if aspectRatio!=Keep original}}
Output aspect ratio: {{aspectRatio}} — extend or crop the background, never stretch the product.
{{/if}}
{{#if aspectRatio=Keep original}}
Keep the original aspect ratio.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Changing, redrawing or re-typing any part of the packaging
- Adding text, badges, logos or extra products
- Plastic-looking over-smoothing, halos, mismatched shadows
{{/if}}
{{#unless avoid}}
AVOID
- Changing, redrawing or re-typing any part of the packaging
- Adding text, badges, logos or extra products
- Plastic-looking over-smoothing, halos, mismatched shadows
{{/unless}}

NEGATIVE PROMPT
altered label, misspelled text, redrawn logo, distorted shape, halo edges, blurry, oversaturated, watermark, extra objects, cartoon`,
    tips: [
      "Upload your product photo first, then paste this prompt in the same message. Use the sharpest, most evenly lit photo you have — phone in daylight near a window works.",
      "If the label changes, reply: “Use my original photo pixels for the product; only change the background.”",
      "For the Amazon main image, also run the result through remove.bg and check it on a pure white canvas in Canva.",
    ],
  },
];
